/**
 * Centralized Google API client for consistent authentication and error handling
 */

const GOOGLE_MAPS_API_KEY = import.meta.env["VITE_GOOGLE_MAPS_API_KEY"];

if (!GOOGLE_MAPS_API_KEY) {
  throw new Error("VITE_GOOGLE_MAPS_API_KEY environment variable is not set");
}

/**
 * Base Google API client with proper authentication headers
 */
class GoogleApiClient {
  private apiKey: string;

  constructor(apiKey: string) {
    this.apiKey = apiKey;
  }

  /**
   * Make a request to Google's Geocoding API
   */
  async geocode(address: string): Promise<any> {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
      address,
    )}&key=${this.apiKey}`;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Geocoding API request failed: ${response.status}`);
    }

    const data = await response.json();
    if (data.status !== "OK") {
      throw new Error(
        `Geocoding API error: ${data.status} - ${data.error_message || "Unknown error"}`,
      );
    }

    return data;
  }

  /**
   * Reverse geocode coordinates to get a human-readable address
   */
  async reverseGeocode(latitude: number, longitude: number): Promise<any> {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${this.apiKey}`;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Reverse geocoding API request failed: ${response.status}`);
    }

    const data = await response.json();
    if (data.status !== "OK") {
      throw new Error(
        `Reverse geocoding API error: ${data.status} - ${data.error_message || "Unknown error"}`,
      );
    }

    return data;
  }

  /**
   * Get timezone information using browser's built-in timezone detection
   * This is more reliable than the legacy Google Time Zone API
   */
  async getTimezoneFromCoordinates(
    _latitude: number,
    _longitude: number,
  ): Promise<{ timezoneId: string; timezoneName: string }> {
    // Use browser's built-in timezone detection as it's more reliable
    // and doesn't require additional API permissions
    const timezoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Get a human-readable timezone name
    const timezoneName =
      new Intl.DateTimeFormat("en", {
        timeZone: timezoneId,
        timeZoneName: "long",
      })
        .formatToParts(new Date())
        .find((part) => part.type === "timeZoneName")?.value || timezoneId;

    return {
      timezoneId,
      timezoneName,
    };
  }

  /**
   * Make a request to Google's Places API
   */
  async searchPlaces(query: string, pageSize: number = 5): Promise<any> {
    const response = await fetch(`https://places.googleapis.com/v1/places:searchText`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": this.apiKey,
        "X-Goog-FieldMask": [
          "places.displayName",
          "places.formattedAddress",
          "places.location",
          "places.types",
          "places.id",
        ].join(","),
      },
      body: JSON.stringify({
        textQuery: query.trim(),
        pageSize,
      }),
    });

    if (!response.ok) {
      throw new Error(`Places search failed: ${response.status}`);
    }

    return response.json();
  }
}

// Export a singleton instance
export const googleApiClient = new GoogleApiClient(GOOGLE_MAPS_API_KEY);

/**
 * Get timezone information from coordinates using browser's built-in timezone detection
 */
export const getTimezoneFromCoordinates = async (
  latitude: number,
  longitude: number,
  _timestamp?: number,
): Promise<{ timezoneId: string; timezoneName: string }> => {
  try {
    return await googleApiClient.getTimezoneFromCoordinates(latitude, longitude);
  } catch (error) {
    console.error("Error getting timezone from coordinates:", error);
    throw error;
  }
};

/**
 * Extract full street address from Google Geocoding response, falling back to neighborhood if not available
 */
const extractLocationComponents = (addressComponents: any[]): string => {
  const components = {
    streetNumber: addressComponents.find((comp: any) => comp.types.includes("street_number"))
      ?.long_name,
    route: addressComponents.find((comp: any) => comp.types.includes("route"))?.long_name,
    neighborhood: addressComponents.find(
      (comp: any) =>
        comp.types.includes("neighborhood") || comp.types.includes("sublocality_level_1"),
    )?.long_name,
    city: addressComponents.find(
      (comp: any) =>
        comp.types.includes("locality") || comp.types.includes("administrative_area_level_2"),
    )?.long_name,
    state: addressComponents.find((comp: any) => comp.types.includes("administrative_area_level_1"))
      ?.long_name,
    zip: addressComponents.find((comp: any) => comp.types.includes("postal_code"))?.long_name,
  };

  const locationParts = [];

  // Prioritize full street address if available
  if (components.streetNumber && components.route) {
    locationParts.push(`${components.streetNumber} ${components.route}`);
  } else if (components.route) {
    locationParts.push(components.route);
  } else if (components.neighborhood) {
    // Fall back to neighborhood if no street address is available
    locationParts.push(components.neighborhood);
  }

  if (components.city) locationParts.push(components.city);
  if (components.state && components.zip) {
    locationParts.push(`${components.state} ${components.zip}`);
  } else if (components.state) {
    locationParts.push(components.state);
  }

  return locationParts.join(", ") || "Current location";
};

/**
 * Get timezone and location name from a location query by first geocoding it, then getting the timezone
 */
export const getTimezoneFromLocation = async (
  locationQuery: string,
): Promise<{ timezoneId: string; timezoneName: string; locationName: string }> => {
  try {
    // First, geocode the location to get coordinates and location name
    const geocodeData = await googleApiClient.geocode(locationQuery);

    if (!geocodeData.results.length) {
      throw new Error(`No location found for query: ${locationQuery}`);
    }

    const location = geocodeData.results[0];
    const { lat, lng } = location.geometry.location;

    // Extract a readable location name from the geocoding response
    const locationName = extractLocationComponents(location.address_components);

    // Then get timezone from coordinates
    const timezoneResult = await getTimezoneFromCoordinates(lat, lng);

    return {
      ...timezoneResult,
      locationName,
    };
  } catch (error) {
    console.error("Error getting timezone from location:", error);
    throw error;
  }
};

/**
 * Reverse geocode coordinates to get a human-readable address
 */
export const reverseGeocode = async (latitude: number, longitude: number): Promise<string> => {
  try {
    const data = await googleApiClient.reverseGeocode(latitude, longitude);

    if (!data.results.length) {
      throw new Error("No address found for the given coordinates");
    }

    // Find the most specific result that includes a street address
    let bestResult = data.results[0];

    for (const result of data.results) {
      const hasStreetNumber = result.address_components.some((comp: any) =>
        comp.types.includes("street_number"),
      );
      const hasRoute = result.address_components.some((comp: any) => comp.types.includes("route"));

      // Prefer results with both street number and route (full street address)
      if (hasStreetNumber && hasRoute) {
        bestResult = result;
        break;
      }
    }

    // Try to get a nice formatted address, fallback to the full formatted address
    const extractedAddress = extractLocationComponents(bestResult.address_components);
    return extractedAddress !== "Current location"
      ? extractedAddress
      : bestResult.formatted_address;
  } catch (error) {
    console.error("Error reverse geocoding coordinates:", error);
    throw error;
  }
};

/**
 * Search for places using the Google Places API
 */
export const searchPlaces = async (query: string, pageSize: number = 5): Promise<any[]> => {
  if (!query.trim()) {
    return [];
  }

  try {
    const data = await googleApiClient.searchPlaces(query, pageSize);

    // Transform the response to match our expected format
    return (
      data.places?.map((place: any) => ({
        id: place.id,
        name: place.displayName?.text || "",
        address: place.formattedAddress || place.displayName?.text || "",
        placeId: place.id,
        location: place.location,
        types: place.types,
      })) || []
    );
  } catch (error) {
    console.error("Error searching places:", error);
    throw error;
  }
};
